import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useCurrentUser } from '@/entities/auth';
import { hasPerm } from '@/shared/hooks/usePerm';
import type { ApiAuthMeStaffPermissions } from '@/shared/types/api/auth';

type Props = {
  children: ReactNode;
};

export const ProtectedRoute = ({ children }: Props) => {
  const { isAuthenticated } = useCurrentUser();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
};

/**
 * Ordered list of section landing routes + the permission required to land
 * on each. Used by `RedirectIfAuth`, `RootRedirect` and `PermGuard` to find
 * the FIRST accessible route for the current user when their default landing
 * page is blocked.
 *
 * KEEP IN SYNC with `Sidebar.tsx`'s `NAV_ITEMS`: every sidebar entry that has
 * a `perm` MUST appear here so a user with only that section's perm lands on
 * it. The order here defines preference (dashboard first for owners, then
 * parcels for workers, then the rest).
 */
const SECTION_LANDINGS: readonly { path: string; perm: string }[] = [
  { path: '/dashboard', perm: 'dashboard.view' },
  { path: '/parcels', perm: 'parcels.view' },
  { path: '/parties', perm: 'parties.view' },
  { path: '/packages', perm: 'packages.view' },
  { path: '/splits', perm: 'splits.view' },
  { path: '/cargo-ids', perm: 'cargo_ids.view' },
  { path: '/warehouse', perm: 'warehouse.view' },
  { path: '/clients', perm: 'clients.view' },
  { path: '/legacy-clients', perm: 'legacy_clients.view' },
  { path: '/support', perm: 'support.view' },
  { path: '/support-staff', perm: 'support_staff.view' },
  { path: '/reports/photo', perm: 'reports.view' },
  { path: '/returns', perm: 'returns.view' },
  { path: '/staff', perm: 'staff.view' },
  { path: '/roles', perm: 'roles.view' },
  { path: '/pricing', perm: 'pricing.view' },
  { path: '/categories', perm: 'categories.view' },
  { path: '/faqs', perm: 'faqs.view' },
  { path: '/broadcast', perm: 'broadcast.view' },
];

/**
 * Pick the first sidebar entry the user can access. Super users always land
 * on `/dashboard`. When `permissions` is still loading (null) we conservatively
 * route to `/parcels` (everyone-but-the-most-restricted default). Only when
 * permissions are loaded AND the user has NOTHING in `SECTION_LANDINGS` do we
 * surface `/no-access` (which is itself rendered inside the AdminShell so the
 * topbar/logout/sidebar remain reachable).
 *
 * Exported so `NoAccess` and route-level redirects can reuse it.
 */
export const firstAccessiblePath = (
  permissions: ApiAuthMeStaffPermissions | null,
): string => {
  if (!permissions) return '/parcels';
  if (permissions.__super__ === true) return '/dashboard';
  const hit = SECTION_LANDINGS.find((s) => hasPerm(permissions, s.perm));
  return hit?.path ?? '/no-access';
};

/**
 * Index route handler. Sends authenticated users to the first sidebar entry
 * they can access (via `firstAccessiblePath`) instead of a hardcoded
 * `/parcels` — that hardcoded default would bounce a user without
 * `parcels.view` to `/no-access` even when they had other accessible sections.
 *
 * While `/auth/me` is in flight (`permissions === null` but `isAuthenticated`)
 * we render nothing rather than redirect, so the page doesn't flicker through
 * `/parcels` on its way to the correct landing.
 */
export const RootRedirect = () => {
  const { isAuthenticated, permissions } = useCurrentUser();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (permissions === null) return null;
  return <Navigate to={firstAccessiblePath(permissions)} replace />;
};

export const RedirectIfAuth = ({ children }: Props) => {
  const { isAuthenticated, user, permissions } = useCurrentUser();
  if (isAuthenticated) {
    if (permissions) {
      return <Navigate to={firstAccessiblePath(permissions)} replace />;
    }
    const target = user?.role === 'OWNER' ? '/dashboard' : '/parcels';
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
};

type PermGuardProps = Props & {
  /** Dotted permission path required to render this route. */
  perm: string;
};

/**
 * Route-level permission gate. Super users always pass. Anyone else needs the
 * exact permission path; if they lack it we redirect to the first section
 * landing they CAN access (so workers without `dashboard.view` get bounced to
 * `/parcels` etc.), or to `/no-access` as a last resort.
 *
 * The `/auth/me` payload may still be loading on first render — when
 * `permissions` is null we render nothing rather than redirect, otherwise we
 * would bounce authenticated users to `/login`'s redirect loop.
 */
export const PermGuard = ({ perm, children }: PermGuardProps) => {
  const { isAuthenticated, permissions } = useCurrentUser();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (permissions === null) return null;
  if (permissions.__super__ === true) return <>{children}</>;
  if (hasPerm(permissions, perm)) return <>{children}</>;
  return <Navigate to={firstAccessiblePath(permissions)} replace />;
};
